'use client';

import { useEffect, useRef, useState } from 'react';
import { IconChevronRight } from '@tabler/icons-react';
import { motion, AnimatePresence } from 'framer-motion';
import classNames from 'classnames';
import Button from '../../../form/Button';

interface Property {
  name: string;
  url: string;
}

interface City {
  name: string;
  properties: Property[];
}

interface DropdownPlacesProps {
  title: string;
  citiesData: City[];
  isDark?: boolean;
}

export default function DropdownPlaces({ title, citiesData, isDark }: DropdownPlacesProps) {
  const [open, setOpen] = useState(false);
  const [activeCity, setActiveCity] = useState<string | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const hoverTimeoutRef = useRef<NodeJS.Timeout>();

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setOpen(false);
        setActiveCity(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleMouseEnter = (cityName: string) => {
    clearTimeout(hoverTimeoutRef.current);
    hoverTimeoutRef.current = setTimeout(() => {
      setActiveCity(cityName);
    }, 100); // ajustable
  };

  const handleMouseLeave = () => {
    clearTimeout(hoverTimeoutRef.current);
  };

  const handlePropertyClick = (url: string) => {
    if (url) {
      window.open(url, '_self');
    } else {
      console.warn('Esta propiedad no tiene URL definida');
    }
  };

  return (
    <div className="relative inline-block" ref={dropdownRef}>
      <Button
        className={classNames(isDark ? "" : "!text-white hover:!text-primary active:!text-primary focus:!text-primary")}
        onClick={() => setOpen(!open)}
        variant="transparent"
        size="wide"
        endIcon={<i className={`gg-chevron-down`} />}
      >
        {title}
      </Button>

      {open && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0 }}
          onMouseLeave={() => {
            setOpen(false);
            setActiveCity(null);
          }}
          className="absolute left-0 top-full mt-2 bg-white rounded-lg shadow-lg p-6 flex z-50"
          style={{ width: activeCity ? '600px' : '300px' }}
        >
          {/* Columna de Ciudades */}
          <div className={`transition-all duration-300 ease-in-out ${activeCity ? 'w-1/2 pr-6 border-r' : 'w-full'}`}>
            <h3 className="text-lg mb-4 font-semibold text-[#212121]">Lugares</h3>
            <ul className="space-y-2">
              {citiesData.map((city) => (
                <li
                  key={city.name}
                  className={`flex text-[#212121] justify-between items-center px-2 py-2 rounded cursor-pointer font-semibold hover:bg-gray-100 ${activeCity === city.name ? 'bg-gray-100' : ''
                    }`}
                  onMouseEnter={() => handleMouseEnter(city.name)}
                  onMouseLeave={handleMouseLeave}
                >
                  <p className={`font-sans ${activeCity === city.name ? 'font-semibold' : 'font-normal'
                    }`}>{city.name}</p>
                  {activeCity === city.name && (
                    <motion.span
                      initial={{ x: -10, opacity: 0 }}
                      animate={{ x: 0, opacity: 1 }}
                      exit={{ opacity: 0 }}
                      className="ml-2"
                    >
                      <IconChevronRight size={18} />
                    </motion.span>
                  )}
                </li>
              ))}
            </ul>
          </div>

          {/* Columna de Propiedades */}
          <AnimatePresence mode="wait">
            {activeCity && (
              <motion.div
                key={activeCity}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
                transition={{ duration: 0.15 }}
                className="w-1/2 pl-6"
              >
                <h3 className="text-lg font-semibold mb-4 border-b pb-2 text-[#212121]">{activeCity}</h3>
                <ul className="space-y-2">
                  {citiesData
                    .find((c) => c.name === activeCity)
                    ?.properties.map((property) => (
                      <li
                        key={property.name}
                        className="px-2 py-2 hover:bg-gray-100 rounded cursor-pointer text-[#212121]"
                        onClick={() => handlePropertyClick(property.url)}
                      >
                        <div className="flex justify-between items-center">
                          <p>{property.name}</p>
                        </div>
                      </li>
                    ))}
                </ul>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      )}
    </div>
  );
}
